import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-hot-toast';
import { useLocation, useNavigate } from 'react-router-dom';
import Tabs from '~/components/Tabs';
import Breadcrumb from '~/components/Breadcrumbs/Breadcrumb';
import { MdOutlineEdit, MdOutlineFileDownload, MdOutlineLink } from 'react-icons/md';
import { RiDeleteBinLine } from 'react-icons/ri';
import { FaArrowUp, FaArrowDown, FaTriangleExclamation, FaRotateLeft } from 'react-icons/fa6';
import AddFileModal from '~/components/Files/AddFile';
import {
    DocumentMetadata,
    StandardDocument,
    deactivateDocument,
    fetchDocumentMetadata,
    fetchDocuments,
    moveDocument,
    reactivateDocument,
} from '~/services/documents';

// The four document types legacy manages, in the order its menu lists them,
// behind an All tab: the filters above the tabs apply to every type, so there
// has to be a list that shows matches from all four at once.
const TABS: { name: string; docType: string }[] = [
    { name: 'All', docType: '' },
    { name: 'Flyers', docType: 'Flyer' },
    { name: 'Handouts', docType: 'Handout' },
    { name: 'Forms', docType: 'Form' },
    { name: 'Letters', docType: 'Letter' },
];

const Files: React.FC = () => {
    // The sidebar links each document type to its own tab (/files?type=Form),
    // so the four items under Files land where their label says they will.
    const location = useLocation();
    const navigate = useNavigate();
    const tabFromUrl = useMemo(() => {
        const type = new URLSearchParams(location.search).get('type');
        // No type means All, which is also where /files lands.
        const index = type ? TABS.findIndex(tab => tab.docType === type) : 0;
        return index === -1 ? 1 : index + 1;
    }, [location.search]);

    const [openTab, setOpenTab] = useState<number>(tabFromUrl);

    // Clicking a sidebar item while already on the page only changes the
    // query string, so follow it rather than staying on the current tab.
    useEffect(() => {
        setOpenTab(tabFromUrl);
    }, [tabFromUrl]);
    const [documents, setDocuments] = useState<StandardDocument[]>([]);
    const [metadata, setMetadata] = useState<DocumentMetadata | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [includeInactive, setIncludeInactive] = useState(false);
    const [search, setSearch] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [languageFilter, setLanguageFilter] = useState('');
    const [editing, setEditing] = useState<StandardDocument | null>(null);
    const [busyId, setBusyId] = useState<number | null>(null);

    // Empty on the All tab, which is what widens the list to every type.
    const activeDocType = TABS[openTab - 1].docType;
    const searchTerm = search.trim();
    const showingAllTypes = activeDocType === '';

    // Keep the URL in step with the tab so the page can be linked and
    // reloaded on the tab the user is actually looking at.
    const selectTab = useCallback(
        (tab: number) => {
            setOpenTab(tab);
            const docType = TABS[tab - 1].docType;
            navigate(docType ? `/files?type=${docType}` : '/files', {
                replace: true,
            });
        },
        [navigate],
    );

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const docs = await fetchDocuments({
                docType: activeDocType || undefined,
                includeInactive,
                search: searchTerm || undefined,
                category: categoryFilter || undefined,
                language: languageFilter || undefined,
            });
            setDocuments(docs);
        } catch (err: any) {
            console.error('Error loading documents:', err);
            setError(err?.message || 'Failed to load documents.');
        } finally {
            setLoading(false);
        }
    }, [
        activeDocType,
        includeInactive,
        searchTerm,
        categoryFilter,
        languageFilter,
    ]);

    useEffect(() => {
        load();
    }, [load]);

    // The form's dropdowns are unusable without this, and a single failed
    // fetch used to leave every one of them silently empty for the life of
    // the page. Retry when a modal opens so reopening the form recovers.
    const loadMetadata = useCallback(() => {
        fetchDocumentMetadata()
            .then(setMetadata)
            .catch(err => {
                console.error('Error loading document options:', err);
                setMetadata(null);
                toast.error(
                    'Could not load the type, category and language options. '
                    + 'Reopen the form to try again.',
                );
            });
    }, []);

    useEffect(() => {
        loadMetadata();
    }, [loadMetadata]);

    // Tab badges count what the current filters would show for each type, so
    // the number matches the list the user lands on -- and during a search
    // they show which types the matches are in.
    const [counts, setCounts] = useState<Record<string, number>>({});
    useEffect(() => {
        let cancelled = false;
        Promise.all(
            TABS.map(tab =>
                fetchDocuments({
                    docType: tab.docType || undefined,
                    includeInactive,
                    search: searchTerm || undefined,
                    category: categoryFilter || undefined,
                    language: languageFilter || undefined,
                })
                    .then(docs => [tab.docType, docs.length] as const)
                    .catch(() => [tab.docType, 0] as const),
            ),
        ).then(entries => {
            if (!cancelled) setCounts(Object.fromEntries(entries));
        });
        return () => {
            cancelled = true;
        };
    }, [
        includeInactive,
        searchTerm,
        categoryFilter,
        languageFilter,
        documents.length,
    ]);

    const tabNames = useMemo(
        () => TABS.map(tab => ({ name: tab.name, count: counts[tab.docType] ?? 0 })),
        [counts],
    );

    // Letters are staff templates and never reach the materials panel, so the
    // category warning doesn't apply on that tab.
    const uncategorizedCount = useMemo(
        () => documents.filter(d => d.needs_category && d.is_active).length,
        [documents],
    );

    // A saved document can change type and leave the current tab, and every
    // save renumbers the type, so reload rather than patch the row in place.
    const handleSaved = () => {
        load();
    };

    const handleMove = async (doc: StandardDocument, direction: 'up' | 'down') => {
        setBusyId(doc.id);
        try {
            await moveDocument(doc.id, direction);
            await load();
        } catch (err: any) {
            console.error('Error reordering document:', err);
            toast.error(err?.message || 'Could not reorder this document');
        } finally {
            setBusyId(null);
        }
    };

    const handleDeactivate = async (doc: StandardDocument) => {
        const confirmed = window.confirm(
            `Deactivate "${doc.name}"? It stops appearing for clients and staff, ` +
                'but the file is kept and you can reactivate it later.',
        );
        if (!confirmed) return;

        setBusyId(doc.id);
        toast.loading('Deactivating...');
        try {
            await deactivateDocument(doc.id);
            toast.dismiss();
            toast.success('Document deactivated.');
            await load();
        } catch (err: any) {
            toast.dismiss();
            console.error('Error deactivating document:', err);
            toast.error(err?.message || 'Could not deactivate this document');
        } finally {
            setBusyId(null);
        }
    };

    const handleCopyLink = async (doc: StandardDocument) => {
        try {
            await navigator.clipboard.writeText(doc.url);
            toast.success('Link copied to clipboard');
        } catch (err) {
            console.error('Error copying document link:', err);
            toast.error('Could not copy the link');
        }
    };

    const handleReactivate = async (doc: StandardDocument) => {
        setBusyId(doc.id);
        toast.loading('Reactivating...');
        try {
            await reactivateDocument(doc.id);
            toast.dismiss();
            toast.success('Document reactivated.');
            await load();
        } catch (err: any) {
            toast.dismiss();
            console.error('Error reactivating document:', err);
            toast.error(err?.message || 'Could not reactivate this document');
        } finally {
            setBusyId(null);
        }
    };

    return (
        <>
            <Breadcrumb pageName='Files' />

            <section className='data-table-common rounded-sm border border-stroke bg-white py-4 shadow-default dark:border-strokedark dark:bg-boxdark'>
                {/*
                    The filters sit above the tabs because they apply to every
                    type: sitting between the tabs and the table, a search read
                    as though it only looked inside the open tab.
                */}
                <div className='flex flex-wrap items-center justify-between gap-4 px-8 pb-4'>
                    <div className='flex flex-wrap items-center gap-4'>
                        <input
                            type='text'
                            value={search}
                            onChange={e => setSearch(e.target.value)}
                            placeholder='Search all documents by name...'
                            className='rounded-[4px] border border-[#949599] p-[8px] text-sm'
                        />
                        <select
                            value={categoryFilter}
                            onChange={e => setCategoryFilter(e.target.value)}
                            className='rounded-[4px] border border-[#949599] p-[8px] text-sm'
                            aria-label='Filter by category'
                        >
                            <option value=''>All categories</option>
                            {(metadata?.categories || []).map(c => (
                                <option key={c.id} value={String(c.id)}>
                                    {c.label}
                                </option>
                            ))}
                            {/*
                                Most active documents have no category and so
                                never reach a client dashboard, which is what
                                the warning below the tabs is about. This is
                                how staff find them.
                            */}
                            <option value='none'>No category</option>
                        </select>
                        <select
                            value={languageFilter}
                            onChange={e => setLanguageFilter(e.target.value)}
                            className='rounded-[4px] border border-[#949599] p-[8px] text-sm'
                            aria-label='Filter by language'
                        >
                            <option value=''>All languages</option>
                            {(metadata?.languages || []).map(l => (
                                <option key={l.value} value={l.value}>
                                    {l.label}
                                </option>
                            ))}
                        </select>
                        <label className='flex cursor-pointer items-center gap-2 text-sm'>
                            <input
                                type='checkbox'
                                checked={includeInactive}
                                onChange={e => setIncludeInactive(e.target.checked)}
                                className='rounded border-gray-300 text-primary focus:ring-primary'
                            />
                            Show inactive
                        </label>
                        {(searchTerm || categoryFilter || languageFilter) && (
                            <button
                                type='button'
                                onClick={() => {
                                    setSearch('');
                                    setCategoryFilter('');
                                    setLanguageFilter('');
                                }}
                                className='text-sm text-primary underline'
                            >
                                Clear filters
                            </button>
                        )}
                    </div>

                    <AddFileModal
                        metadata={metadata}
                        onRequestMetadata={loadMetadata}
                        defaultDocType={activeDocType || undefined}
                        onSaved={handleSaved}
                    />
                </div>

                <div className='flex items-center justify-between gap-6 border-t border-stroke px-8 pb-4 pt-3'>
                    <Tabs
                        openTab={openTab}
                        setOpenTab={selectTab}
                        tabNames={tabNames}
                        className='!mb-0 w-full'
                    ></Tabs>

                    <p className='shrink-0 text-[12px] text-gray-600'>
                        {loading
                            ? 'Loading...'
                            : `${documents.length} document${documents.length === 1 ? '' : 's'}`}
                    </p>
                </div>

                {/*
                    The client dashboard only lists documents in the
                    guidelines / forms / other categories, so an uncategorized
                    one is invisible there no matter how it is ordered. Rather
                    than pick categories on staff's behalf, flag them here
                    (HHLTB-2808).
                */}
                {uncategorizedCount > 0 && (
                    <div className='mx-8 mb-2 flex items-start gap-2 rounded border border-warning bg-warning/10 px-4 py-3 text-sm'>
                        <FaTriangleExclamation className='mt-0.5 shrink-0 text-warning' />
                        <span>
                            {uncategorizedCount} active{' '}
                            {uncategorizedCount === 1 ? 'document has' : 'documents have'}{' '}
                            no category, so {uncategorizedCount === 1 ? 'it does' : 'they do'}{' '}
                            not appear on client dashboards. Edit{' '}
                            {uncategorizedCount === 1 ? 'it' : 'them'} to choose a category.
                        </span>
                    </div>
                )}

                {error ? (
                    <div className='px-8 py-8 text-center'>
                        <p className='mb-4 text-red'>{error}</p>
                        <button
                            onClick={load}
                            className='rounded-lg bg-primary px-6 py-2 font-medium text-white hover:bg-primary/90'
                        >
                            Try Again
                        </button>
                    </div>
                ) : !loading && documents.length === 0 ? (
                    <p className='px-8 py-8 text-center text-gray-600'>
                        {searchTerm || categoryFilter || languageFilter
                            ? `No ${
                                showingAllTypes
                                    ? 'documents'
                                    : TABS[openTab - 1].name.toLowerCase()
                            } match these filters.`
                            : showingAllTypes
                              ? 'No documents yet.'
                              : `No ${TABS[openTab - 1].name.toLowerCase()} yet.`}
                    </p>
                ) : (
                    <div className='max-w-full overflow-x-auto px-8'>
                        <table className='w-full table-auto'>
                            <thead>
                                <tr className='bg-gray-2 text-left'>
                                    {/*
                                        Order only means something within a
                                        single type, since the arrows swap
                                        sequence among same-type siblings. The
                                        All tab shows the type instead of an
                                        order it cannot honour.
                                    */}
                                    <th className='px-4 py-3 text-sm font-medium text-black'>
                                        {showingAllTypes ? 'Type' : 'Order'}
                                    </th>
                                    <th className='px-4 py-3 text-sm font-medium text-black'>
                                        Name
                                    </th>
                                    <th className='px-4 py-3 text-sm font-medium text-black'>
                                        Category
                                    </th>
                                    <th className='px-4 py-3 text-sm font-medium text-black'>
                                        Language
                                    </th>
                                    <th className='px-4 py-3 text-sm font-medium text-black'>
                                        Medical Reasons
                                    </th>
                                    <th className='px-4 py-3 text-sm font-medium text-black'>
                                        Status
                                    </th>
                                    <th className='px-4 py-3 text-sm font-medium text-black'>
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {documents.map((doc, index) => (
                                    <tr
                                        key={doc.id}
                                        className={busyId === doc.id ? 'opacity-50' : ''}
                                    >
                                        <td className='border-b border-[#eee] px-4 py-3'>
                                            {showingAllTypes ? (
                                                <span className='text-black'>
                                                    {doc.doc_type}
                                                </span>
                                            ) : (
                                                <div className='flex items-center gap-1'>
                                                    <button
                                                        onClick={() => handleMove(doc, 'up')}
                                                        disabled={index === 0 || busyId !== null}
                                                        className='rounded p-1 text-primary hover:bg-primary/10 disabled:opacity-30'
                                                        title='Move up'
                                                    >
                                                        <FaArrowUp size={12} />
                                                    </button>
                                                    <button
                                                        onClick={() => handleMove(doc, 'down')}
                                                        disabled={
                                                            index === documents.length - 1 ||
                                                            busyId !== null
                                                        }
                                                        className='rounded p-1 text-primary hover:bg-primary/10 disabled:opacity-30'
                                                        title='Move down'
                                                    >
                                                        <FaArrowDown size={12} />
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                        <td className='border-b border-[#eee] px-4 py-3'>
                                            <p className='text-black'>{doc.name}</p>
                                            {doc.description && (
                                                <p className='text-[12px] text-gray-600'>
                                                    {doc.description}
                                                </p>
                                            )}
                                            {!doc.has_file && (
                                                <p className='text-[12px] text-red'>
                                                    No file attached
                                                </p>
                                            )}
                                        </td>
                                        <td className='border-b border-[#eee] px-4 py-3'>
                                            {doc.category ? (
                                                <span className='text-black'>
                                                    {doc.category.label}
                                                </span>
                                            ) : (
                                                <span className='text-gray-600'></span>
                                            )}
                                            {doc.needs_category && doc.is_active && (
                                                <span
                                                    className='ml-1 inline-flex align-middle text-warning'
                                                    title='Not shown on client dashboards without a category'
                                                >
                                                    <FaTriangleExclamation size={12} />
                                                </span>
                                            )}
                                        </td>
                                        <td className='border-b border-[#eee] px-4 py-3 text-black'>
                                            {doc.language}
                                        </td>
                                        <td className='border-b border-[#eee] px-4 py-3 text-black'>
                                            {doc.medical_reasons
                                                .map(r => r.name)
                                                .join(', ')}
                                        </td>
                                        <td className='border-b border-[#eee] px-4 py-3'>
                                            <span
                                                className={`inline-block rounded-full px-2.5 py-0.5 text-[12px] font-medium ${
                                                    doc.is_active
                                                        ? 'bg-success/10 text-success'
                                                        : 'bg-gray-200 text-gray-600'
                                                }`}
                                            >
                                                {doc.is_active ? 'Active' : 'Inactive'}
                                            </span>
                                        </td>
                                        <td className='border-b border-[#eee] px-4 py-3'>
                                            <div className='flex items-center gap-3'>
                                                {doc.has_file && (
                                                    <a
                                                        href={doc.url}
                                                        target='_blank'
                                                        rel='noreferrer'
                                                        className='text-primary hover:text-primary/70'
                                                        title='Open'
                                                    >
                                                        <MdOutlineFileDownload size={18} />
                                                    </a>
                                                )}
                                                {doc.has_file && (
                                                    <button
                                                        onClick={() => handleCopyLink(doc)}
                                                        className='text-primary hover:text-primary/70'
                                                        title='Copy link'
                                                    >
                                                        <MdOutlineLink size={18} />
                                                    </button>
                                                )}
                                                <button
                                                    onClick={() => setEditing(doc)}
                                                    className='text-primary hover:text-primary/70'
                                                    title='Edit'
                                                >
                                                    <MdOutlineEdit size={18} />
                                                </button>
                                                {doc.is_active ? (
                                                    <button
                                                        onClick={() => handleDeactivate(doc)}
                                                        disabled={busyId !== null}
                                                        className='text-red hover:opacity-70 disabled:opacity-30'
                                                        title='Deactivate'
                                                    >
                                                        <RiDeleteBinLine size={18} />
                                                    </button>
                                                ) : (
                                                    <button
                                                        onClick={() => handleReactivate(doc)}
                                                        disabled={busyId !== null}
                                                        className='text-primary hover:text-primary/70 disabled:opacity-30'
                                                        title='Reactivate'
                                                    >
                                                        <FaRotateLeft size={16} />
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </section>

            {editing && (
                <AddFileModal
                    metadata={metadata}
                    onRequestMetadata={loadMetadata}
                    document={editing}
                    isOpen={!!editing}
                    onClose={() => setEditing(null)}
                    onSaved={handleSaved}
                    showTrigger={false}
                />
            )}
        </>
    );
};

export default Files;
